import { Button, Container, List, Paper, Tab, Tabs } from '@mui/material';
import { Subheader, TabPanel } from 'components';
import { useEffect, useMemo, useState } from 'react';
import EventDetailsComponent from './EventDetailsComponent';
import EventVideosComponent from './EventVideosComponent';
import {
	useAcademyEvents,
	useAlerts,
	useEventVideos,
	useUploadFileInMemory
} from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import VideoModal from './VideoModal';
import { isEmpty, set } from 'lodash';
import { validateEventVideo } from 'validations/event_video';
import EventVideoItem from './EventVideoItem';

const CREATE_BUTTON_CONFIG = [
	{
		url: 'events',
		title: 'Event Details'
	},
	{
		url: 'event_videos',
		title: 'Event Videos'
	}
];

const AcademyEventDetails = () => {
	const { id } = useParams();
	const [tabValue, setTabValue] = useState(0);
	const [createConfig, setCreateConfig] = useState(CREATE_BUTTON_CONFIG[0]);
	const { findAcademyEvent, academyEvent, isLoading, setAcademyEvent } =
		useAcademyEvents({ id });
	const [showVideoModal, setShowVideoModal] = useState(false);

	const { memoryFile, removeFile, handleUploadFile, clearAllFiles } =
		useUploadFileInMemory();
	const {
		eventVideo,
		handleChange: handleChangeVideo,
		clearResource: clearResourceVideo,
		createEventVideo,
		setEventVideo,
		resortEventVideos,
		deleteEventVideo
	} = useEventVideos({});
	const { showAlertError, showAlertSuccess } = useAlerts();

	const navigate = useNavigate();

	const handleTabChange = (_, newValue) => {
		setTabValue(newValue);
		setCreateConfig(CREATE_BUTTON_CONFIG[newValue]);
	};

	const createButton = useMemo(() => {
		return createConfig.url === 'events' ? (
			<Button
				color="primary"
				variant="contained"
				onClick={() =>
					navigate(
						`/academies/${academyEvent?.academy?.id}/event/${academyEvent.id}/edit`
					)
				}
			>
				{'Edit Event'}
			</Button>
		) : (
			<Button
				color="primary"
				variant="contained"
				onClick={() => setShowVideoModal(true)}
			>
				{'Add Video'}
			</Button>
		);
	}, [createConfig, academyEvent]);

	const handleDeleteFile = async (type) => {
		removeFile();
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		if (name === 'cloudinary_url' && !isEmpty(memoryFile)) {
			removeFile();
		}

		handleChangeVideo(e);
	};

	const handleSubmit = async (e) => {
		const event_data = {
			...eventVideo,
			video_url: eventVideo.cloudinary_url || memoryFile?.video_url?.url || '',
			event_id: id
		};

		const valid = validateEventVideo(event_data, memoryFile);
		if (valid.isValid) {
			await createEventVideo(event_data, memoryFile);

			if (!isLoading) {
				setShowVideoModal(false);
				clearResourceVideo();
				clearAllFiles();
				findAcademyEvent(id);
			}
		} else {
			showAlertError(valid.messages[0]);
		}
	};

	const handleOnDragEnd = (result) => {
		if (!result) {
			return;
		}

		setAcademyEvent((prev) => ({
			...prev,
			event_videos: result
		}));

		resortEventVideos({ id: academyEvent.id, event_videos: result });
	};

	const handleDeleteClick = async (video) => {
		await deleteEventVideo(video);

		setAcademyEvent((prev) => ({
			...prev,
			event_videos: prev.event_videos.filter((v) => v.id !== video.id)
		}));
	};

	useEffect(() => {
		if (id) {
			findAcademyEvent(id);
			setEventVideo((prev) => ({ ...prev, event_id: id }));
		}
	}, [id]);

	return (
		<>
			<Subheader
				title={isLoading ? 'Loading...' : academyEvent.name}
				buttons={[createButton]}
			/>
			<Container>
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					aria-label="Event Details"
				>
					<Tab label="Event Details" tabIndex={0} />
					<Tab label="Event Videos" tabIndex={1} />
				</Tabs>
				<Paper>
					<TabPanel value={tabValue} index={0}>
						<EventDetailsComponent event={academyEvent} />
					</TabPanel>
					<TabPanel value={tabValue} index={1}>
						<EventVideosComponent
							id={'event-videos-scrollable-list'}
							list={academyEvent?.event_videos ?? []}
							ListContainer={List}
							onDragEnd={handleOnDragEnd}
							ListItem={({ item, index }) => (
								<EventVideoItem
									item={item}
									index={index}
									handleDeleteClick={handleDeleteClick}
								/>
							)}
						/>
					</TabPanel>
				</Paper>
			</Container>

			<VideoModal
				open={showVideoModal}
				onClose={() => setShowVideoModal(false)}
				submit={handleSubmit}
				eventVideo={eventVideo}
				memoryFile={memoryFile}
				handleChange={onChange}
				handleDelete={handleDeleteFile}
				handleUploadFile={handleUploadFile}
				setEventVideo={setEventVideo}
			/>
		</>
	);
};

export default AcademyEventDetails;
